import {
  TAnimationType,
  TImageAsset,
  TLocale,
  TScrollAnimation,
  TVideoAsset,
  TVideoType,
  videoTypes,
} from '@/types/global';
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { events } from './events';
import { TTypographyAnimationTypes } from '@/components/atoms/Typography/Typography';
import { emailEstudioNotEncoded } from './constants';

/**
 * Checks if the given string contains any forbidden HTML tags
 * @param html  		 - string to check
 * @returns {boolean}
 */
export const isDangerousHtml = (html: string): boolean => {
  const forbiddenTags =
    /<script\b|<\/script>|<iframe\b|<\/iframe>|<object\b|<\/object>|<embed\b|<\/embed>|<link\b|<\/link>|<meta\b|<\/meta>/i;
  return forbiddenTags.test(html);
};

/**
 * Gets the string if it is safe, otherwise returns an empty string
 * @param text
 * @returns {string}
 */
export const getSafeHtml = (text: string): string =>
  isDangerousHtml(text) ? '' : text;

/**
 * Replaces all <br> tags with <br> and a span with nbsp; inside in order to add an indent after the line break
 * @param htmlString  - string to modify
 * @param indentSize  - number of spaces to add after the line break
 * @returns {string}
 */
export const addIndentAfterBrToHtmlString = (
  htmlString: string,
  indentSize: number = 4
): string => {
  const spaces = '&nbsp;'.repeat(indentSize);
  return htmlString.replace(/<br\s*\/?>/g, `<br><span>${spaces}</span>`);
};

export const scrollToTop = () =>
  window.scrollTo({ top: 0, behavior: 'smooth' });

export const scrollToRef = (ref: HTMLElement | null, smooth = true) => {
  if (ref && typeof window !== 'undefined') {
    const offset = Math.max(window.innerHeight * 0.1, 80);
    window.scrollTo({
      top: ref.getBoundingClientRect().top + window.scrollY - offset,
      behavior: smooth ? 'smooth' : 'instant'
    });
  }
};

type Align = 'top' | 'center' | 'bottom';
type Position = Align | `${Align} ${number}px` | '';

export const scrollToElement = ({
  target,
  smooth = true,
  position = '',
  scrollToTop = false,
  extraHeightUnderHeader = 0,
  scrollMobile = true,
}: {
  target: HTMLElement | string | null;
  smooth?: boolean;
  position?: Position;
  scrollToTop?: boolean;
  extraHeightUnderHeader?: number;
  scrollMobile?: boolean;
}) => {
  if (!target || typeof window === 'undefined' || typeof document === 'undefined') return;

  const el =
    typeof target === 'string'
      ? (document.querySelector(target) as HTMLElement | null)
      : target;
  if (!el) return;

  // Fixed/sticky header height
  const header = document.querySelector('header') as HTMLElement | null;
  const headerOffset = header ? header.clientHeight + (extraHeightUnderHeader || 0) : 0;

  // Parse alignment string
  const parsePos = (pos: string | undefined, fallbackOffset = 0) => {
    if (!pos) return { align: 'top' as Align, offset: fallbackOffset };
    const [alignRaw, offsetRaw] = pos.split(' ');
    const align = (alignRaw || 'top') as Align;
    const offset = offsetRaw?.endsWith('px') ? parseFloat(offsetRaw) : 0;
    return { align, offset };
  };

  const posString = scrollToTop ? `top ${headerOffset}px` : (position || undefined);
  const { align, offset } = parsePos(posString, scrollToTop ? headerOffset : 0);

  // Compute a single absolute Y target in document space
  const computeAbsoluteY = () => {
    const baseTop = (() => {
      const smoother = ScrollSmoother.get();
      if (smoother) {
        // GSAP's consistent document-space position
        return smoother.offset(el) as number;
      }
      // Native document-space position
      const rect = el.getBoundingClientRect();
      const currentY = window.scrollY || window.pageYOffset || 0;
      return currentY + rect.top;
    })();

    // Where should the element's TOP land within the viewport?
    const viewportAnchor = (() => {
      if (align === 'top') return 0 + offset + headerOffset;
      if (align === 'center') return Math.max(0, (window.innerHeight - headerOffset) / 2 + offset);
      // align === 'bottom'
      return Math.max(0, window.innerHeight - headerOffset + offset);
    })();

    // We want elementTop - viewportAnchor to be the final scrollTop.
    // (i.e., position element.top at the chosen anchor while accounting for header)
    const y = Math.max(0, baseTop - viewportAnchor);
    return y;
  };

  // If layout is mid-transition, wait one frame for stability
  requestAnimationFrame(() => {
    const targetY = computeAbsoluteY();
    const smoother = ScrollSmoother.get();

    if (smoother) {
      smoother.scrollTo(targetY, smooth); // pass absolute number, not a "position" string
    } else {
      if (!scrollMobile) return;
      window.scrollTo({ top: targetY, behavior: smooth ? 'smooth' : 'auto' });
    }
  });
};

export const getPageType = (isProject: boolean): 'project' | 'page' => {
  return isProject ? 'project' : 'page';
};

export const toggleModal = (eventName: string): void => {
  window.dispatchEvent(new Event(eventName));
};

export const toggleVirtualTourModal = (): void => {
  window.dispatchEvent(new CustomEvent(events.toggleVirtualTourModal));
};

export const toggleGallerySliderModal = (imageSrc?: string): void => {
  window.dispatchEvent(
    new CustomEvent(events.toggleGallerySliderModal, { detail: imageSrc || '' })
  );
};

export const handleClickToggleGallerySliderModal = (
  e: React.MouseEvent | Event,
  src: string
) => {
  const sliderReference = src;
  e.stopPropagation();
  e.preventDefault();
  toggleGallerySliderModal(sliderReference);
};

export const includesParagraphOrDiv = (html: string): boolean => {
  return /<p\b|<div\b/i.test(html);
};

export function isTVideoType(value: string): value is TVideoType {
  return (videoTypes as readonly string[]).includes(value);
}

export const capitalizeFirstLetter = (str: string | undefined): string => {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : '';
};

export const capitalizeFirstLettersAllWords = (str: string | undefined): string => {
  return str
    ? str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';
};

export const capitaliseEachWord = (input: string): string => {
  return input.trim().split(/\s+/).map(capitalizeFirstLetter).join(' ');
};

export const getKbFromBytes = (bytes: number): number => {
  return bytes / 1024;
};

export const isVideoForListCorrectSize = (
  bytes: number | string,
  maxSizeKb: number = 2000
): boolean => {
  const sizeBytes = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;

  const sizeKb = getKbFromBytes(sizeBytes);
  const isCorrectSize = sizeKb <= maxSizeKb;
  return isCorrectSize;
};

export const getBaseUrl = () => {
  const url = process.env.NEXT_PUBLIC_SITE_URL || '';
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

/**
 * @param dateString --> DATE FORMAT: YYYY-MM-DD
 */
export const isDateExpired = (dateString: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiryDate = new Date(dateString);
  return today > expiryDate;
};

export const getAltFromImageSrc = (src: string): string => {
  const fileName = src?.split('/').pop()?.split('.')[0] || '';
  return fileName.replace(/-/g, ' ');
};

export const isVideoAsset = (asset: TVideoAsset | TImageAsset): boolean => {
  return Boolean((asset as TVideoAsset)?.vimeoId);
};

export type IAnimatedLinesResult = {
  animatedLinesDesktop?: string[];
  animatedLinesMobile?: string[];
  animatedLines?: string[];
};

export function getAnimatedLines({
  input,
  mobileSize,
  desktopSize,
  isInTwoColumns = false,
}: {
  input: string;
  mobileSize: string;
  desktopSize: string;
  isInTwoColumns?: boolean;
}): IAnimatedLinesResult {
  if (typeof input !== 'string') {
    return {};
  }

  const hasBr = input.includes('<br>');

  const breakCharsMobile: { [key: string]: number } = {
    '4xl': 27,
    '3xl': 33,
  };

  const mobileBreakChars = breakCharsMobile[mobileSize] || 25;
  let desktopBreakChars = desktopSize === '2xl' ? 60 : 55;
  // base BannerText with 2 columns:
  desktopBreakChars = isInTwoColumns ? desktopBreakChars / 1.8 : desktopBreakChars;

  // Helper: chunk a line without breaking words (UNCHANGED)
  const chunkLine = (text: string, maxLength: number): string[] => {
    const words = text.split(/\s+/);
    const chunks: string[] = [];
    let currentLine = '';

    for (const word of words) {
      if ((currentLine + ' ' + word).trim().length <= maxLength) {
        currentLine = (currentLine + ' ' + word).trim();
      } else {
        if (currentLine) chunks.push(currentLine);
        currentLine = word;
      }
    }

    if (currentLine) chunks.push(currentLine);
    return chunks;
  };

  // Clean mobile-specific <br> and split (UNCHANGED helper used for desktop)
  const getLines = (
    str: string,
    brRegex: RegExp,
    cleanupRegex: RegExp
  ): string[] =>
    str
      .split(brRegex)
      .map((line) => line.replace(cleanupRegex, '').trim())
      .filter((line) => line);

  // --- MOBILE: safest possible splitter ---
  // If there are <br data-mobile>, split by those; otherwise split by <br>.
  // We do NOT trim or change the segments; we also drop the delimiter itself.
  const splitMobileSafely = (str: string): string[] => {
    const hasMobileBr = /<br\s+data-mobile[^>]*>/i.test(str);
    const regex = hasMobileBr ? /(<br\s+data-mobile[^>]*>)/i : /(<br\s*\/?>)/i;

    const parts = str.split(regex);
    const out: string[] = [];
    let buf = '';

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue;

      // If this part is a break tag (matches capture)
      if (regex.test(part)) {
        // push accumulated content as a segment (exact substring), then reset
        out.push(buf);
        buf = '';
        // do NOT include the break tag itself in the segment to avoid double <br> when rendering
      } else {
        buf += part;
      }
    }
    // push any leftover
    if (buf !== '') out.push(buf);
    // Do NOT trim, return as-is for hydration safety
    return out.filter((s) => s !== '');
  };

  // MOBILE: use the safe splitter
  const mobileSegments = splitMobileSafely(input);

  // Any segment containing HTML tags is kept literally; otherwise chunk like before.
  // This ensures your first item (with <span data-maintain> and <h1>) is EXACT and unmodified.
  const animatedLinesMobile = mobileSegments.flatMap((seg) => {
    const containsHtml = /<[^>]+>/.test(seg);
    if (containsHtml) {
      return [seg]; // keep literal, no trimming, no escaping changes
    }
    return chunkLine(seg, mobileBreakChars);
  });

  // Desktop lines (UNCHANGED)
  let animatedLinesDesktop: string[];

  if (hasBr) {
    // Respect <br> and clean data-mobile breaks
    animatedLinesDesktop = getLines(input, /<br>/, /<br data-mobile[^>]*>/g);
  } else {
    // Fallback to mobile logic and chunking for desktop
    animatedLinesDesktop = mobileSegments.flatMap((line) =>
      // For desktop we keep your previous behavior: chunk by length
      chunkLine(line, desktopBreakChars)
    );
  }

  return {
    animatedLinesMobile,
    animatedLinesDesktop,
  };
}




export const getFormatedDate = (inputDate: string): string | undefined => {
  const date = new Date(inputDate);
  const day = String(date.getDate()).padStart(2, '0'); // Add leading zero for single digits
  const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() is zero-based, so add 1
  const year = date.getFullYear();

  return day && month && year ? `${day}/${month}/${year}` : undefined;
};

export const getDefaultAnimation = (animation: TAnimationType) => {
  if (animation === ('default' as TAnimationType)) {
    return 'fadeIn';
  } else return animation;
};

export const getDefaultScrollAnimation = (
  scrollAnimation: TScrollAnimation
) => {
  if (scrollAnimation === ('default' as TScrollAnimation)) {
    return 'none';
  } else return scrollAnimation;
};

export const getDefaultHightlightTextAnimation = (
  animation: TTypographyAnimationTypes
) => {
  if (animation === ('default' as TTypographyAnimationTypes)) {
    return 'none';
  } else return animation;
};


export const openEmailClient = (subject?: string, body?: string) => {
  const subjectParam = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  const bodyParam = body ? (subject ? `&body=${encodeURIComponent(body)}` : `?body=${encodeURIComponent(body)}`) : '';
  const mailtoLink = `mailto:${emailEstudioNotEncoded}${subjectParam}${bodyParam}`;
  window.location.href = mailtoLink;
}

export const replaceVisualisation = (text: string): string => {
  return text.replace(/visualisation/gi, 'visualization');
}

export const replaceServicesOldWithNew = (text: string, locale: TLocale): string => {
  if (locale === 'es') {
    return text.replace(/branding/gi, 'branding inmobiliario')
               .replace(/Visualización/gi, 'visualización arquitectónica')
  }
  return text.replace(/branding/gi, 'property branding')
             .replace(/visualisation/gi, 'architectural visualization')
             .replace(/visualization/gi, 'architectural visualization')
}

export const replaceServicesNewWithOld = (text: string, locale: TLocale): string => {
  if (locale === 'es') {
    return text.replace(/branding inmobiliario/gi, 'branding')
               .replace(/visualización arquitectónica/gi, 'visualization')
  }
  return text.replace(/property branding/gi, 'branding')
             .replace(/architectural visualization/gi, 'visualisation')
};